import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { crmApi, errMsg } from '@crm/api';

const initialState = {
  results: [],
  searchStatus: 'idle',
  searchError: null,
  current: null,
  currentStatus: 'idle',
  currentError: null,
  saveStatus: 'idle',
  saveError: null,
  lastOrder: null,
};

export const searchEvents = createAsyncThunk(
  'crmEvents/search',
  async ({ branchId, q }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).get('/events', { params: { q } });
      return data.items ?? [];
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось найти события'));
    }
  },
);

export const fetchEventForSale = createAsyncThunk(
  'crmEvents/fetchOne',
  async ({ branchId, eventId }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).get(`/events/${eventId}`);
      return data.event;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Событие не найдено'));
    }
  },
);

export const createEventOrder = createAsyncThunk(
  'crmEvents/createOrder',
  async ({ branchId, eventId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).post(`/events/${eventId}/orders`, payload);
      return data;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось оформить заказ'));
    }
  },
);

export const createEventPromocode = createAsyncThunk(
  'crmEvents/createPromocode',
  async ({ branchId, eventId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await crmApi(branchId).post(`/events/${eventId}/promocodes`, payload ?? {});
      return data;
    } catch (err) {
      const data = err?.response?.data;
      return rejectWithValue({
        message: errMsg(err, 'Не удалось создать промокод'),
        code: data?.error ?? null,
        details: data?.details ?? null,
      });
    }
  },
);

const eventsSlice = createSlice({
  name: 'crmEvents',
  initialState,
  reducers: {
    clearCurrentEvent(state) {
      state.current = null;
      state.currentStatus = 'idle';
      state.currentError = null;
      state.saveStatus = 'idle';
      state.saveError = null;
      state.lastOrder = null;
    },
    clearEventsErrors(state) {
      state.searchError = null;
      state.currentError = null;
      state.saveError = null;
    },
    resetSaveStatus(state) {
      state.saveStatus = 'idle';
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchEvents.pending, (state) => {
        state.searchStatus = 'loading';
        state.searchError = null;
      })
      .addCase(searchEvents.fulfilled, (state, action) => {
        state.searchStatus = 'succeeded';
        state.results = action.payload;
      })
      .addCase(searchEvents.rejected, (state, action) => {
        state.searchStatus = 'failed';
        state.searchError = action.payload;
      })
      .addCase(fetchEventForSale.pending, (state) => {
        state.currentStatus = 'loading';
        state.currentError = null;
      })
      .addCase(fetchEventForSale.fulfilled, (state, action) => {
        state.currentStatus = 'succeeded';
        state.current = action.payload;
      })
      .addCase(fetchEventForSale.rejected, (state, action) => {
        state.currentStatus = 'failed';
        state.currentError = action.payload;
      })
      .addCase(createEventOrder.pending, (state) => {
        state.saveStatus = 'loading';
        state.saveError = null;
      })
      .addCase(createEventOrder.fulfilled, (state, action) => {
        state.saveStatus = 'succeeded';
        state.current = action.payload.event ?? state.current;
        state.lastOrder = action.payload.order ?? null;
      })
      .addCase(createEventOrder.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.saveError = action.payload;
      })
      .addCase(createEventPromocode.fulfilled, (state, action) => {
        if (action.payload.event) state.current = action.payload.event;
      });
  },
});

export const { clearCurrentEvent, clearEventsErrors, resetSaveStatus } = eventsSlice.actions;
export default eventsSlice.reducer;

export const selectEventResults = (state) => state.crmEvents.results;
export const selectEventSearchStatus = (state) => state.crmEvents.searchStatus;
export const selectCurrentEvent = (state) => state.crmEvents.current;
export const selectCurrentEventStatus = (state) => state.crmEvents.currentStatus;
export const selectEventSaveStatus = (state) => state.crmEvents.saveStatus;
export const selectEventSaveError = (state) => state.crmEvents.saveError;
export const selectLastOrder = (state) => state.crmEvents.lastOrder;
